// Pure (no-React, no-r3f) camera-framing math, extracted from CameraRig.tsx
// so the "does this actually fit the room in frame" formula is testable
// without a canvas — get the vertical/horizontal FOV trig wrong here and the
// room either clips through the near plane or sits tiny in a sea of black,
// and nothing in the app would say why.

/** Half-diagonal of the room's bounding box — the radius of a sphere that
 *  contains the whole room regardless of orientation. */
export function roomBoundingRadius(Lx: number, Ly: number, H: number): number {
  return Math.hypot(Lx, Ly, H) / 2;
}

/** Camera distance (from the room centre) at which a sphere of `radius`
 *  fills `targetFill` of the SMALLER screen axis, given the camera's
 *  vertical FOV and the viewport's aspect ratio. Horizontal FOV is derived
 *  from vertical FOV + aspect (the standard relation for a perspective
 *  camera); the smaller of the two axis-FOVs is the binding constraint —
 *  on a portrait-ish viewport that's usually the horizontal one, so
 *  computing only from vertical FOV (as a naive version would) frames too
 *  tight and lets the room's sides clip out of view. */
export function distanceToFitSphere(
  radius: number, fovVerticalRad: number, aspect: number, targetFill: number,
): number {
  const fovH = 2 * Math.atan(Math.tan(fovVerticalRad / 2) * aspect);
  const fovMin = Math.min(fovVerticalRad, fovH);
  return radius / (targetFill * Math.tan(fovMin / 2));
}

/** What to multiply the camera's distance by when the viewport's aspect goes
 *  from `fromAspect` to `toAspect`, so the room keeps the share of the
 *  binding screen axis it had before.
 *
 *  A ratio, not a fit: it preserves whatever framing the user orbited or
 *  zoomed to rather than snapping back to a default. The room's radius and
 *  fill cancel out of the quotient, so neither is a parameter. On a landscape
 *  viewport that only gets wider or narrower the vertical FOV binds on both
 *  sides and the answer is exactly 1: the camera does not move at all. */
export function aspectReframeScale(
  fovVerticalRad: number, fromAspect: number, toAspect: number,
): number {
  return distanceToFitSphere(1, fovVerticalRad, toAspect, 1)
    / distanceToFitSphere(1, fovVerticalRad, fromAspect, 1);
}

/** Dashboard thumbnail aspect — matches ProjectCard's `aspect-[16/10]`. */
export const THUMBNAIL_ASPECT = 16 / 10;

/** 45° plan azimuth + true-isometric elevation (~35.26°) — the usual CAD corner view. */
const THUMBNAIL_AZIMUTH_RAD = Math.PI / 4;
const THUMBNAIL_ELEVATION_RAD = Math.atan(1 / Math.sqrt(2));

/** Camera world position for a room-centred CAD thumbnail at the given aspect. */
export function thumbnailCadCameraPosition(
  target: [number, number, number],
  roomRadius: number,
  fovVerticalDeg: number,
  aspect: number,
  targetFill = 0.72,
): [number, number, number] {
  const fovV = (fovVerticalDeg * Math.PI) / 180;
  const dist = distanceToFitSphere(roomRadius, fovV, aspect, targetFill);
  const horiz = Math.cos(THUMBNAIL_ELEVATION_RAD) * dist;
  const dx = horiz * Math.cos(THUMBNAIL_AZIMUTH_RAD);
  const dy = Math.sin(THUMBNAIL_ELEVATION_RAD) * dist;
  const dz = horiz * Math.sin(THUMBNAIL_AZIMUTH_RAD);
  return [target[0] + dx, target[1] + dy, target[2] + dz];
}
